// @ts-nocheck
import { ReactNode, useEffect, useState } from 'react';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { useReducedMotion } from '@/components/animations';

interface ParallaxLayer {
  /** Content or element for this layer */
  element?: ReactNode;
  /** Parallax speed multiplier (negative = opposite direction) */
  speed: number;
  /** Initial offset */
  offset?: { x: number; y: number };
  /** Layer opacity */
  opacity?: number;
  /** Z-index for layering */
  zIndex?: number;
}

interface ParallaxBackgroundProps {
  children: ReactNode;
  /** Preset theme for the parallax effect */
  preset?: 'geometric' | 'circles' | 'waves' | 'stars' | 'gradient-orbs';
  /** Custom layers (overrides preset) */
  layers?: ParallaxLayer[];
  /** Whether to respond to mouse movement */
  mouseParallax?: boolean;
  /** Intensity of mouse parallax effect */
  intensity?: number;
}

export default function ParallaxBackground({
  children,
  preset = 'geometric',
  layers,
  mouseParallax = true,
  intensity = 20,
}: ParallaxBackgroundProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!mouseParallax || reduceMotion) return;

    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseParallax, reduceMotion]);

  const getTransform = (speed: number) => {
    if (reduceMotion) return 'translate(0, 0)';
    const x = mousePosition.x * intensity * speed;
    const y = mousePosition.y * intensity * speed;
    return `translate(${x}px, ${y}px)`;
  };

  // Generate preset layers
  const getPresetLayers = (): ReactNode => {
    const primaryColor = theme.colors.indigo[isDark ? 4 : 6];
    const secondaryColor = theme.colors.cyan[isDark ? 4 : 5];
    const accentColor = theme.colors.violet[isDark ? 4 : 5];

    if (preset === 'geometric') {
      return (
        <>
          {/* Layer 1 - Large shapes */}
          <Box
            style={{
              position: 'absolute',
              top: '10%',
              right: '5%',
              width: 300,
              height: 300,
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              opacity: 0.15,
              filter: 'blur(40px)',
              transform: getTransform(0.5),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          />
          {/* Layer 2 - Medium shapes */}
          <Box
            style={{
              position: 'absolute',
              bottom: '20%',
              left: '10%',
              width: 200,
              height: 200,
              borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
              background: `linear-gradient(45deg, ${accentColor}, ${primaryColor})`,
              opacity: 0.12,
              filter: 'blur(30px)',
              transform: getTransform(-0.3),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          />
          {/* Layer 3 - Small accent */}
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              right: '20%',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: secondaryColor,
              opacity: 0.1,
              filter: 'blur(20px)',
              transform: getTransform(0.8),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          />
        </>
      );
    }

    if (preset === 'circles') {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              style={{
                position: 'absolute',
                top: `${20 + i * 15}%`,
                left: `${10 + i * 20}%`,
                width: 150 - i * 20,
                height: 150 - i * 20,
                borderRadius: '50%',
                border: `2px solid ${[primaryColor, secondaryColor, accentColor][i % 3]}`,
                opacity: 0.2 - i * 0.03,
                transform: getTransform(0.2 + i * 0.15),
                transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
              }}
            />
          ))}
        </>
      );
    }

    if (preset === 'waves') {
      return (
        <>
          <Box
            component="svg"
            viewBox="0 0 1440 320"
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              opacity: 0.1,
              transform: getTransform(0.2),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          >
            <path
              fill={primaryColor}
              d="M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,138.7C672,128,768,160,864,181.3C960,203,1056,213,1152,197.3C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </Box>
          <Box
            component="svg"
            viewBox="0 0 1440 320"
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              opacity: 0.08,
              transform: getTransform(-0.3),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          >
            <path
              fill={secondaryColor}
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,234.7C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </Box>
        </>
      );
    }

    if (preset === 'stars') {
      const stars = [...Array(30)].map((_, i) => ({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.2,
        speed: Math.random() * 0.5 + 0.1,
      }));

      return (
        <>
          {stars.map((star, i) => (
            <Box
              key={i}
              style={{
                position: 'absolute',
                top: `${star.y}%`,
                left: `${star.x}%`,
                width: star.size,
                height: star.size,
                borderRadius: '50%',
                backgroundColor: isDark ? 'white' : primaryColor,
                opacity: star.opacity,
                transform: getTransform(star.speed),
                transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
              }}
            />
          ))}
        </>
      );
    }

    if (preset === 'gradient-orbs') {
      return (
        <>
          <Box
            style={{
              position: 'absolute',
              top: '-10%',
              right: '-10%',
              width: '50%',
              height: '50%',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${primaryColor} 0%, transparent 70%)`,
              opacity: 0.3,
              filter: 'blur(60px)',
              transform: getTransform(0.4),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: '-15%',
              left: '-10%',
              width: '45%',
              height: '45%',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${secondaryColor} 0%, transparent 70%)`,
              opacity: 0.25,
              filter: 'blur(50px)',
              transform: getTransform(-0.3),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              top: '40%',
              left: '60%',
              width: '30%',
              height: '30%',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${accentColor} 0%, transparent 70%)`,
              opacity: 0.2,
              filter: 'blur(40px)',
              transform: getTransform(0.6),
              transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
            }}
          />
        </>
      );
    }

    return null;
  };

  return (
    <Box
      style={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        backgroundColor: isDark ? theme.colors.dark[8] : theme.colors.gray[0],
      }}
    >
      {/* Parallax layers */}
      <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {layers ? (
          layers.map((layer, index) => (
            <Box
              key={index}
              style={{
                position: 'absolute',
                ...layer.offset,
                opacity: layer.opacity ?? 1,
                zIndex: layer.zIndex ?? index,
                transform: getTransform(layer.speed),
                transition: reduceMotion ? 'none' : 'transform 0.1s ease-out',
              }}
            >
              {layer.element}
            </Box>
          ))
        ) : (
          getPresetLayers()
        )}
      </Box>

      {/* Content */}
      <Box style={{ position: 'relative', zIndex: 10 }}>
        {children}
      </Box>
    </Box>
  );
}
